/**
 * CorrelationShareMenu — Reddit-style share popover for a public correlation.
 *
 * Lives on Weekly cards (next to the comments count) and inside the
 * CorrelationModal (engagement footer). Exposes:
 *  - Native share sheet (Web Share API, mobile) when available
 *  - Copy link
 *  - X (Twitter), Facebook, LinkedIn, WhatsApp, Reddit, Email
 *
 * The shared URL is `${origin}/api/share/correlation/<id>` — a server-rendered
 * OG-tagged route. Social crawlers see the unfurl HTML; humans get a 302 to
 * /weekly#item-<id> which the ScrollManager + deep-link handler pick up.
 */
import React, { useMemo, useState } from "react";
import { Share2, Copy, Check, Link2, Mail, ImageIcon, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { toast } from "sonner";
import { api } from "../lib/api";
import WeeklyItemCardThumb, { isRenderableCardData } from "./WeeklyItemCardThumb";

function shareUrl(correlation) {
  const origin = (typeof window !== "undefined" && window.location?.origin) || "";
  return `${origin}/api/share/correlation/${correlation.id}`;
}

function shareTitle(correlation) {
  return `${correlation.title} — Correlate`;
}

function shareBlurb(correlation) {
  // Prefer the author's caption, then bullets, then a static fallback.
  if (correlation.caption) return correlation.caption;
  if (Array.isArray(correlation.bullets) && correlation.bullets.length > 0) {
    return correlation.bullets.slice(0, 2).join(" · ");
  }
  return "A correlation across traditions on Correlate.";
}

// Brand-faithful SVG glyphs so the menu doesn't rely on emoji.
const Icons = {
  X: (p) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" {...p}>
      <path d="M18.244 2H21l-6.52 7.45L22 22h-6.78l-4.78-6.27L4.8 22H2.04l7.02-8.02L1.6 2h6.93l4.32 5.71L18.244 2Zm-2.37 18.3h1.86L7.22 3.62H5.23l10.64 16.68Z" />
    </svg>
  ),
  Facebook: (p) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" {...p}>
      <path d="M22 12.07C22 6.51 17.52 2 12 2S2 6.51 2 12.07c0 5.02 3.66 9.18 8.44 9.93v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.77l-.44 2.9h-2.33V22c4.78-.75 8.44-4.91 8.44-9.93Z" />
    </svg>
  ),
  LinkedIn: (p) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" {...p}>
      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05c.47-.9 1.63-1.86 3.36-1.86 3.6 0 4.27 2.37 4.27 5.45v6.3ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.12 20.45H3.56V9h3.56v11.45ZM22.23 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.46c.98 0 1.77-.77 1.77-1.73V1.73C24 .77 23.21 0 22.23 0Z" />
    </svg>
  ),
  WhatsApp: (p) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" {...p}>
      <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1-.2.3-.8.9-1 1.1-.2.2-.4.2-.7.1-.3-.1-1.2-.4-2.3-1.4-.8-.7-1.4-1.6-1.6-1.9-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 .9-1 2.3 0 1.4 1 2.7 1.2 2.9.1.2 2 3 4.8 4.2.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.2-.2-.5-.3ZM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.4 1.3 4.9L2 22l5.3-1.4c1.4.8 3 1.2 4.7 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2Z" />
    </svg>
  ),
  Reddit: (p) => (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" {...p}>
      <path d="M22 12.05a2.13 2.13 0 0 0-3.62-1.51 10.4 10.4 0 0 0-5.66-1.8l.97-4.55 3.16.67a1.52 1.52 0 1 0 .15-.78l-3.52-.74c-.1-.02-.2 0-.28.05a.38.38 0 0 0-.18.23l-1.08 5.08a10.42 10.42 0 0 0-5.74 1.8 2.13 2.13 0 1 0-2.34 3.5 4.27 4.27 0 0 0-.05.66c0 3.36 3.9 6.08 8.71 6.08s8.71-2.72 8.71-6.08c0-.22-.02-.44-.05-.66A2.13 2.13 0 0 0 22 12.05Zm-14.06 1.5a1.52 1.52 0 1 1 3.03 0 1.52 1.52 0 0 1-3.03 0Zm8.46 4a5.49 5.49 0 0 1-3.4 1.06 5.49 5.49 0 0 1-3.4-1.06.39.39 0 0 1 .55-.55 4.7 4.7 0 0 0 2.85.86c1 0 2.04-.27 2.85-.86a.39.39 0 0 1 .55.55Zm.06-2.48a1.52 1.52 0 1 1 0-3.03 1.52 1.52 0 0 1 0 3.03Z" />
    </svg>
  ),
};

export default function CorrelationShareMenu({ correlation, testIdPrefix = "share" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Card generation state — starts as "unresolved" (null) when the popover
  // opens on a card-less post so the user is prompted; flips to true/false
  // after they answer. Uses the strict renderable-shape check so that posts
  // with partial or malformed card_data still prompt (matches the check
  // WeeklyItemCardThumb uses to decide whether to render the card at all).
  const startedWithCard = isRenderableCardData(correlation?.card_data);
  const [cardChoice, setCardChoice] = useState(startedWithCard ? "yes" : null);
  const [generatingCard, setGeneratingCard] = useState(false);
  const showCardPrompt = cardChoice === null;

  const url = useMemo(() => shareUrl(correlation), [correlation]);
  const title = useMemo(() => shareTitle(correlation), [correlation]);
  const blurb = useMemo(() => shareBlurb(correlation), [correlation]);

  const generateCard = async () => {
    setGeneratingCard(true);
    try {
      // /oracle/card-data attaches the generated card_data to the save when
      // save_id is provided, so the OG-unfurl route will pick it up on the
      // next external share.
      await api.post("/oracle/card-data", { save_id: correlation.id });
      setCardChoice("yes");
      toast.success("Card generated — social previews will include it.");
    } catch (err) {
      // Soft-fail — the share still works, just without a card.
      toast.error(err?.response?.data?.detail || "Couldn't generate the card. Sharing without it.");
      setCardChoice("no");
    } finally {
      setGeneratingCard(false);
    }
  };

  // Web Share API — fires the OS-native share sheet on mobile (iOS/Android).
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const nativeShare = async () => {
    try {
      await navigator.share({ title, text: blurb, url });
      setOpen(false);
    } catch (err) {
      // User cancelled — silent. Any other error → toast.
      if (err && err.name !== "AbortError") {
        toast.error("Could not share.");
      }
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Could not copy link.");
    }
  };

  // Copy the caption text (author's framing + link) — the composed blurb
  // people actually want to paste into an Instagram story caption, a Threads
  // post, a WhatsApp message, or anywhere they've downloaded the card image
  // and now need words to go with it. Format: title, blank line, blurb,
  // blank line, link. Newlines survive across every clipboard target
  // (including mobile share sheets and native compose fields).
  const [captionCopied, setCaptionCopied] = useState(false);
  const copyCaption = async () => {
    const text = [title, blurb, url].filter(Boolean).join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setCaptionCopied(true);
      toast.success("Caption copied — paste it wherever.");
      setTimeout(() => setCaptionCopied(false), 1600);
    } catch {
      toast.error("Could not copy caption.");
    }
  };

  // Intent-URL builders for each network — opened in a new tab.
  const openIntent = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=620,height=620");
    setOpen(false);
  };

  const handlers = [
    { id: "twitter", label: "X (Twitter)", Icon: Icons.X,
      onClick: () => openIntent(`https://twitter.com/intent/tweet?text=${encodeURIComponent(blurb)}&url=${encodeURIComponent(url)}`) },
    { id: "facebook", label: "Facebook", Icon: Icons.Facebook,
      onClick: () => openIntent(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`) },
    { id: "linkedin", label: "LinkedIn", Icon: Icons.LinkedIn,
      onClick: () => openIntent(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`) },
    { id: "whatsapp", label: "WhatsApp", Icon: Icons.WhatsApp,
      onClick: () => openIntent(`https://wa.me/?text=${encodeURIComponent(`${title} — ${url}`)}`) },
    { id: "reddit", label: "Reddit", Icon: Icons.Reddit,
      onClick: () => openIntent(`https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`) },
    { id: "email", label: "Email", Icon: Mail,
      onClick: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${blurb}\n\n${url}`)}`;
        setOpen(false);
      } },
  ];

  // Stop event-bubble so clicking the share button on a card doesn't ALSO
  // expand the card / open the modal underneath.
  const stop = (e) => { e.stopPropagation(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-trigger`}
          onClick={stop}
          aria-label={startedWithCard || cardChoice === "yes" ? "Share (share card ready)" : "Share"}
          title={startedWithCard || cardChoice === "yes" ? "Share — share card ready" : "Share"}
          className="inline-flex items-center gap-1.5 mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
        >
          <span className="relative inline-flex">
            <Share2 size={13} strokeWidth={2} />
            {/* Gold dot — persistent indicator that this post has a
                renderable share card attached. Rendered whenever the DB
                already carries card_data (survives logout, close/reopen,
                anything — it's a property of the post, not local state)
                or the user just generated one this session. */}
            {(startedWithCard || cardChoice === "yes") && (
              <span
                data-testid={`${testIdPrefix}-card-ready-dot`}
                aria-hidden="true"
                className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] shadow-[0_0_0_1.5px_hsl(var(--card))]"
              />
            )}
          </span>
          <span>Share</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-72 p-1"
        onClick={stop}
        data-testid={`${testIdPrefix}-popover`}
      >
        {/* Card status header inside the popover. Three states:
            1. no card yet, unresolved → prompt to generate
            2. card exists (from DB or just-generated) → affirming banner
            3. user opted "no" this session → nothing */}
        {showCardPrompt && (
          <div
            data-testid={`${testIdPrefix}-card-prompt`}
            className="px-3 pt-3 pb-2 border-b border-border/70"
          >
            <div className="serif text-[15px] leading-snug text-foreground mb-3">
              Would you like to generate a share card for this post?
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid={`${testIdPrefix}-card-yes`}
                onClick={generateCard}
                disabled={generatingCard}
                className="flex-1 inline-flex items-center justify-center gap-1.5 mono text-[10px] uppercase tracking-[0.16em] px-3 py-2 rounded border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 transition-colors disabled:opacity-50"
              >
                {generatingCard ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <ImageIcon size={12} />
                    Yes, make one
                  </>
                )}
              </button>
              <button
                type="button"
                data-testid={`${testIdPrefix}-card-no`}
                onClick={() => setCardChoice("no")}
                disabled={generatingCard}
                className="flex-1 mono text-[10px] uppercase tracking-[0.16em] px-3 py-2 rounded border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors disabled:opacity-50"
              >
                No, just share
              </button>
            </div>
          </div>
        )}
        {/* "Card ready" affirming banner — shown when a card exists (either
            already saved on the post OR just generated in this session).
            The small preview thumbnail is itself the ShareCardModal opener
            (WeeklyItemCardThumb wires up its own onClick), so tapping the
            preview blows it up full-size with Download + Instagram Story
            options. */}
        {!showCardPrompt && (startedWithCard || cardChoice === "yes") && (
          <div
            data-testid={`${testIdPrefix}-card-ready`}
            className="px-3 pt-3 pb-3 border-b border-border/70"
          >
            <div className="flex items-center gap-2 text-[hsl(var(--primary))] mb-2.5">
              <Check size={12} strokeWidth={2.5} />
              <span className="mono text-[10px] uppercase tracking-[0.18em]">
                Share card ready — tap to preview or download
              </span>
            </div>
            {/* Card preview wrapper — WeeklyItemCardThumb renders at its
                own fixed intrinsic width (220px). We just flex-center it
                inside the popover; NO inner width wrapper (an inner width
                smaller than the thumb's intrinsic 220px was overflowing
                and shifted the preview off-center). */}
            <div
              className="flex justify-center"
              data-testid={`${testIdPrefix}-card-preview`}
            >
              <WeeklyItemCardThumb item={correlation} />
            </div>
            <p className="mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground text-center mt-2.5">
              Tap card to preview · download · post to IG stories
            </p>
          </div>
        )}
        <ul className="text-[14px]" style={{ opacity: showCardPrompt ? 0.45 : 1, pointerEvents: showCardPrompt ? "none" : "auto" }}>
          {canNativeShare && (
            <li>
              <button
                type="button"
                data-testid={`${testIdPrefix}-native`}
                onClick={nativeShare}
                className="w-full px-3 py-2 flex items-center gap-3 hover:bg-secondary/60 rounded text-left"
              >
                <Share2 size={14} strokeWidth={1.7} className="text-[hsl(var(--accent))]" />
                Share via…
              </button>
            </li>
          )}
          <li>
            <button
              type="button"
              data-testid={`${testIdPrefix}-copy`}
              onClick={copy}
              className="w-full px-3 py-2 flex items-center gap-3 hover:bg-secondary/60 rounded text-left"
            >
              {copied ? (
                <Check size={14} strokeWidth={1.9} className="text-[hsl(var(--primary))]" />
              ) : (
                <Link2 size={14} strokeWidth={1.7} className="text-[hsl(var(--accent))]" />
              )}
              {copied ? "Copied" : "Copy link"}
            </button>
          </li>
          <li>
            {/* Copy caption — grabs title + author's blurb + the link as one
                pasteable block. Useful for Instagram stories, Threads, and
                anywhere the download-image flow leaves you needing to type
                words afterward. Works whether or not the post has a card. */}
            <button
              type="button"
              data-testid={`${testIdPrefix}-copy-caption`}
              onClick={copyCaption}
              className="w-full px-3 py-2 flex items-center gap-3 hover:bg-secondary/60 rounded text-left"
            >
              {captionCopied ? (
                <Check size={14} strokeWidth={1.9} className="text-[hsl(var(--primary))]" />
              ) : (
                <Copy size={14} strokeWidth={1.7} className="text-[hsl(var(--accent))]" />
              )}
              {captionCopied ? "Copied" : "Copy caption"}
            </button>
          </li>
          <li className="my-1 border-t border-border" aria-hidden />
          {handlers.map(({ id, label, Icon, onClick }) => (
            <li key={id}>
              <button
                type="button"
                data-testid={`${testIdPrefix}-${id}`}
                onClick={onClick}
                className="w-full px-3 py-2 flex items-center gap-3 hover:bg-secondary/60 rounded text-left"
              >
                <Icon className="text-[hsl(var(--accent))]" />
                {label}
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
